import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './nav-flyout.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import hfMark from '../icon/assets/icons/hf-mark.svg';

export default class NavFlyout extends OutlineElement {
  static styles = [super.styles, componentStyles];
  static get properties() {
    return {
      links: { type: Array },
      isOpen: { type: Boolean },
      linkClass: { type: String },
      primeLinks: { type: Array },
      search: { type: Object },
      subLinks: { type: Array },
    };
  }

  constructor() {
    super();
    this.activeItemClass = '';
    this.linkClass = '';
    this.isOpen = false;
    this.primeLinks = [];
    this.subLinks = [];
  }

  firstUpdated() {
    if (this.links.length) {
      this.primeLinks = this.links.filter(link => link.url.length);
      this.subLinks = [
        ...this.links.flatMap(link => {
          return link.sub.length ? link.sub : [];
        }),
      ];
    }

    // Add visibility hidden for accessibility.
    if (!this.isOpen) {
      const navFlyout = this.shadowRoot.querySelector('.nav-flyout__wrapper');
      navFlyout.classList.add("nav-flyout--hidden");
    }
  }

  async updated() {
    this._hideNavFlyout();
  }

  _hideNavFlyout() {
    const navFlyout = this.shadowRoot.querySelector('.nav-flyout__wrapper');
    // Visibility hidden after the transition for accessibility.
    navFlyout.addEventListener('transitionend', () => {
      if (!this.isOpen) {
        navFlyout.classList.add("nav-flyout--hidden");
      }
    });
    if (this.isOpen) {
      navFlyout.classList.remove("nav-flyout--hidden");
    }
  }

  render() {
    return html`<div
      class="${classMap({
        'nav-flyout__wrapper': true,
        [`nav-flyout--open`]: this.isOpen,
        [`nav-flyout--closed`]: !this.isOpen,
      })}"
    >
      <div class="nav-flyout__header">
        <hf-link href="/" target="_self" ariaLabel="HealthFirst">
          <div class="nav-flyout__logo-wrapper">
            <hf-icon class="nav-flyout__logo" name="hf-logo"></hf-icon>
          </div>
        </hf-link>
        <hf-search
          class="nav-flyout__search"
          placeholder="${this.search.text}"
          searchUrl="${this.search.url}"
        ></hf-search>
      </div>
      <div class="nav-flyout__links-wrapper">
        <ul class="nav-flyout__prime-links-wrapper">
          ${this.primeLinks.map(link => {
            return html`
              <li class="nav-flyout__prime-links-item">
                <hf-link
                  href=${link.url}
                  target="_self"
                  styleclass="nav-flyout__link"
                  class="nav-flyout__link">
                  ${link.iconName
                    ? html`
                        <hf-icon
                          name="${link.iconName}"
                          class="nav-flyout__link--icon"
                          height="24"
                          width="24"
                        ></hf-icon>
                      `
                    : nothing}
                  <span class="nav-flyout__link-text">${link.text}</span>
                </hf-link>
              </li>
            `;
          })}
        </ul>
        <ul class="nav-flyout__sub-links-wrapper">
          ${this.subLinks.map(subLink => {
            return html`
              <li class="nav-flyout__sub-links-item">
                <hf-link
                  href=${subLink.url}
                  target="_self"
                  styleclass="nav-flyout__sub-link"
                  class="nav-flyout__sub-link"
                >
                  ${subLink.iconName
                    ? html`
                        <hf-icon
                          name="${subLink.iconName}"
                          class="nav-flyout__sub-link--icon"
                          height="24"
                          width="24"
                        ></hf-icon>
                      `
                    : nothing}
                  <span class="nav-flyout__sub-link-text">${subLink.text}</span>
                </hf-link>
              </li>
            `;
          })}
        </ul>
      </div>
      <span class="nav-flyout__icon">${unsafeHTML(hfMark)}</span>
    </div>`;
  }
}

customElements.define('hf-nav-flyout', NavFlyout);
